import React, { useState } from 'react';
import { C1SignupData } from '../C1SignupFlow';
import { useDraftDocuments } from '../../../hooks/useDraftDocuments';
import { StepHeader, StepNavButtons } from '../components';

const displayName = (fileName: string) =>
  fileName
    .replace(/\.(pdf|docx)$/i, '')
    .replace(/[_-]/g, ' ');

const isDocx = (fileName: string) => fileName.toLowerCase().endsWith('.docx');

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
}

const DocxPreviewPane: React.FC<{ downloadUrl: string; fileName: string }> = ({ downloadUrl, fileName }) => {
  const [loaded, setLoaded] = useState(false);
  // Google's servers fetch from S3 — bypasses browser CORS restriction entirely
  const src = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl)}&embedded=true`;
  return (
    <div style={{ borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
      {!loaded && (
        <div style={{ padding: '32px 24px', textAlign: 'center', color: '#64748B', fontSize: 13 }}>
          Loading document…
        </div>
      )}
      <iframe
        key={downloadUrl}
        src={src}
        title={fileName}
        style={{ width: '100%', height: 640, border: 'none', display: loaded ? 'block' : 'none' }}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

const AustracDocuments: React.FC<StepProps> = ({ data, onNext, onCancel }) => {
  const [previewDoc, setPreviewDoc] = useState<{ key: string; downloadUrl: string; fileName: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const companyName = data.partyName || 'Your Company';
  const sector = data.partySector || '';

  const { documents, uploadAustracDocs } = useDraftDocuments({
    austracOnly: true,
    sector,
  });

  const handleContinue = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await uploadAustracDocs(documents);
      onNext({});
    } catch {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ maxWidth: '100%' }}>
      <StepHeader
        icon="🏛️"
        title="AUSTRAC Documents"
        description="Download and review the AUSTRAC compliance documents relevant to your reporting obligations."
      />

      {/* Info Banner */}
      <div style={{
        background: '#F0F7FF',
        border: '1px solid #BFDBFE',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
        <div>
          <div style={{ color: '#0F2647', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Important for {companyName}
          </div>
          <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
            As a reporting entity under the AML/CTF Act, you are required to maintain an AML/CTF program.
            These documents will help you integrate ARCaml's outsourced CDD services into your compliance framework.
            Please download and retain copies for your records.
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 14,
        padding: '22px 26px',
        marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          marginBottom: 18, paddingBottom: 14,
          borderBottom: '1px solid #F1F5F9',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #0F2647 0%, #1B3A6B 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: '#fff', flexShrink: 0,
          }}>📄</div>
          <h3 style={{ margin: 0, color: '#0F2647', fontSize: 15, fontWeight: 700 }}>
            Compliance Documents
          </h3>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>
            {documents.length} documents
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {documents.map((doc) => (
            <div
              key={doc.key}
              style={{
                display: 'flex', flexDirection: 'column',
                border: '1px solid #E2E8F0',
                borderRadius: 10,
                overflow: 'hidden',
                background: '#F8FAFC',
              }}
            >
              {/* Row */}
              <div className="doc-list__row" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#1E293B', fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{displayName(doc.fileName)}</div>
                </div>
                <div className="doc-list__actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: '#64748B',
                    background: '#E2E8F0', padding: '3px 8px',
                    borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>{doc.fileName.split('.').pop()?.toUpperCase()}</span>
                  <button
                    onClick={() => setPreviewDoc(previewDoc?.key === doc.key ? null : doc)}
                    style={{
                      fontSize: 11, fontWeight: 700, color: '#0F2647',
                      background: previewDoc?.key === doc.key ? '#CBD5E1' : '#E0EDFF',
                      border: 'none', padding: '6px 14px',
                      borderRadius: 20, cursor: 'pointer',
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}
                  >
                    {previewDoc?.key === doc.key ? '✕ Close' : '👁 View'}
                  </button>
                  <a
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 11, fontWeight: 700, color: '#0F2647',
                      background: '#E0EDFF', padding: '6px 14px',
                      borderRadius: 20, textDecoration: 'none',
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}
                  >
                    ⬇ Download
                  </a>
                </div>
              </div>
              {/* Inline preview */}
              {previewDoc?.key === doc.key && (
                isDocx(doc.fileName) ? (
                  <DocxPreviewPane downloadUrl={doc.downloadUrl} fileName={doc.fileName} />
                ) : (
                  <div style={{ borderTop: '1px solid #E2E8F0', background: '#fff' }}>
                    <iframe
                      src={`${doc.downloadUrl}#toolbar=1&navpanes=0`}
                      style={{ width: '100%', height: 600, border: 'none', display: 'block' }}
                      title={doc.fileName}
                    />
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      <StepNavButtons
        onCancel={onCancel}
        onContinue={handleContinue}
        cancelLabel="Back"
        continueLabel="Continue to Activation →"
        loading={isProcessing}
      />
    </div>
  );
};

export default AustracDocuments;
